import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  getClientId, startSession, fetchCompanies, fetchPipeline,
  fundUser, submitKyc as apiSubmitKyc, buyUserShares,
  authRegister, authLogin, authMe, authForgot, authReset, authVerify,
} from '../api';

const AppContext = createContext(null);

const load = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export function AppProvider({ children }) {
  const [user, setUser] = useState(null); // {id, name, wallet, kyc, email, authed}
  const [holdings, setHoldings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [watchlist, setWatchlist] = useState(() => load('af_watchlist', []));

  // Auth modal state (lifted so any component can open it)
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authView, setAuthView] = useState('signup');
  const [resetToken, setResetToken] = useState('');

  useEffect(() => localStorage.setItem('af_watchlist', JSON.stringify(watchlist)), [watchlist]);

  const loadMarkets = useCallback(async () => {
    const [comps, pipe] = await Promise.all([fetchCompanies(), fetchPipeline()]);
    setCompanies(comps);
    setPipeline(pipe);
  }, []);

  const guestSession = useCallback(async () => {
    const sess = await startSession(getClientId());
    setUser({ ...sess });
    setHoldings(sess.holdings || []);
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      await loadMarkets();
      const token = localStorage.getItem('af_token');
      if (token) {
        try {
          const res = await authMe(token);
          setUser(res.user);
          setHoldings(res.user.holdings || []);
        } catch {
          localStorage.removeItem('af_token');
          await guestSession();
        }
      } else {
        await guestSession();
      }
    } catch (e) {
      console.error('bootstrap failed', e);
    } finally {
      setLoading(false);
    }
    // detect password reset link
    const params = new URLSearchParams(window.location.search);
    const rt = params.get('reset');
    if (rt) {
      setResetToken(rt);
      setAuthView('reset');
      setAuthModalOpen(true);
    }
    // detect email verification link
    const vt = params.get('verify');
    if (vt) {
      try {
        await authVerify(vt);
        toast.success('Email verified', { description: 'Your email address has been confirmed.' });
        setUser((u) => (u ? { ...u, emailVerified: true } : u));
      } catch (e) {
        toast.error('Verification failed', { description: e?.response?.data?.detail || 'This link is invalid or has expired.' });
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('verify');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, [loadMarkets, guestSession]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // ---- Live prices: poll the server so everyone sees the same moving market ----
  useEffect(() => {
    if (loading) return undefined;
    const timer = setInterval(async () => {
      try {
        const fresh = await fetchCompanies();
        setCompanies((prev) => {
          // merge live fields onto existing objects to preserve references where possible
          const map = {};
          fresh.forEach((c) => { map[c.id] = c; });
          return prev.map((c) => (map[c.id] ? { ...c, ...map[c.id] } : c));
        });
      } catch {
        /* ignore transient poll errors */
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [loading]);

  const toggleWatchlist = (id) =>
    setWatchlist((w) => (w.includes(id) ? w.filter((x) => x !== id) : [...w, id]));

  const addFunds = async (amount) => {
    if (!user) return;
    const res = await fundUser(user.id, amount);
    setUser((u) => ({ ...u, wallet: res.wallet }));
  };

  const submitKyc = async (data) => {
    if (!user) return { ok: false };
    await apiSubmitKyc(user.id, data);
    setUser((u) => ({ ...u, kyc: 'pending' }));
    return { ok: true };
  };

  const buyShares = async (company, shares, price) => {
    if (!user) return { ok: false, reason: 'nouser' };
    try {
      const res = await buyUserShares(user.id, { companyId: company.id, shares, price });
      setUser((u) => ({ ...u, wallet: res.wallet }));
      setHoldings(res.holdings || []);
      return { ok: true };
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      if (detail.toLowerCase().includes('kyc')) return { ok: false, reason: 'kyc' };
      if (detail.toLowerCase().includes('insufficient')) return { ok: false, reason: 'insufficient' };
      return { ok: false, reason: 'error' };
    }
  };

  // ---- auth actions ----
  const register = async (payload) => {
    try {
      const res = await authRegister({ ...payload, origin: window.location.origin });
      localStorage.setItem('af_token', res.token);
      setUser(res.user);
      setHoldings(res.user.holdings || []);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || 'Registration failed' };
    }
  };

  const login = async (email, password) => {
    try {
      const res = await authLogin(email, password);
      localStorage.setItem('af_token', res.token);
      setUser(res.user);
      setHoldings(res.user.holdings || []);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = async () => {
    localStorage.removeItem('af_token');
    await guestSession();
  };

  const forgotPassword = async (email) => {
    try {
      await authForgot(email, window.location.origin);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || 'Request failed' };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      await authReset(token, password);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || 'Reset failed' };
    }
  };

  const openAuth = (view = 'signup') => { setAuthView(view); setAuthModalOpen(true); };
  const closeAuth = () => {
    setAuthModalOpen(false);
    if (resetToken) {
      setResetToken('');
      const url = new URL(window.location.href);
      url.searchParams.delete('reset');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  };

  return (
    <AppContext.Provider
      value={{
        user,
        loading,
        companies,
        pipeline,
        holdings,
        watchlist,
        toggleWatchlist,
        vaultBalance: user ? user.wallet : 0,
        kycStatus: user ? user.kyc : 'unverified',
        isAuthed: !!(user && user.authed),
        addFunds,
        submitKyc,
        buyShares,
        register,
        login,
        logout,
        forgotPassword,
        resetPassword,
        authModalOpen,
        authView,
        setAuthView,
        resetToken,
        openAuth,
        closeAuth,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
